import React, { useState, useContext } from 'react';
import {
    Table,
    TableContainer,
    Paper,
    TableRow,
    TableCell,
    TableBody,
    TableHead,
    IconButton,
    Box,
    Typography,
    Input,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Edit as EditIcon, Done as DoneIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/all';
import { formatPriceByLocale } from '../../../lib/utils';

type Props = {
    cart: any[];
    saveMovementInCart: (mov: any) => void;
    readOnly?: boolean;
    totalNet: number;
    totalVat: number;
    total: number;
};

export default function SalePointCart({
    cart,
    saveMovementInCart,
    readOnly = true,
    totalNet,
    totalVat,
    total,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [editedAmount, setEditedAmount] = useState<string | null>(null);
    const [editingAmount, setEditingAmount] = useState<boolean>(false);
    const toggleEdit = () => setEditingAmount(!editingAmount);

    /**
     * Decrement amount
     */
    const decrementAmount = (mov: any) => {
        saveMovementInCart({
            ...mov,
            amount: mov.amount + 1,
        });
    };

    // -----| Render |-----
    return (
        <>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">{t('Product')}</TableCell>
                            <TableCell align="center">{t('Amount')}</TableCell>
                            <TableCell align="right">{t('Price')}</TableCell>
                            <TableCell align="right">{t('vat')}</TableCell>
                            <TableCell align="right">{t('Total')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {cart &&
                            cart.map((mov: any) => (
                                <TableRow key={mov?.id}>
                                    <TableCell align="left">
                                        <b>{mov.product.name}</b>
                                    </TableCell>
                                    <TableCell align="center">
                                        {editingAmount ? (
                                            <>
                                                <Input
                                                    className="w-8"
                                                    value={editedAmount}
                                                    onChange={(event: any) =>
                                                        setEditedAmount(
                                                            event.target.value,
                                                        )
                                                    }
                                                    size="small"
                                                />
                                                <IconButton
                                                    onClick={() => {
                                                        saveMovementInCart({
                                                            id: mov?.id,
                                                            amount: -Number(
                                                                editedAmount,
                                                            ),
                                                        });
                                                        toggleEdit();
                                                    }}
                                                >
                                                    <DoneIcon className="w-5 h-5" />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <Box className="flex flex-row content-center items-center justify-center">
                                                <Typography className="w-8">
                                                    {Math.abs(mov.amount)}
                                                </Typography>
                                                <IconButton
                                                    disabled={readOnly}
                                                    onClick={toggleEdit}
                                                >
                                                    <EditIcon className="w-5 h-5" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatPriceByLocale(
                                            mov.finalPrice,
                                            cxt?.sc?.city?.country?.currency,
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {mov.vat} %
                                    </TableCell>
                                    <TableCell align="right">
                                        <b>
                                            {formatPriceByLocale(
                                                Math.abs(mov.total),
                                                cxt?.sc?.city?.country
                                                    ?.currency,
                                            )}
                                        </b>
                                    </TableCell>

                                    {!readOnly && (
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={() =>
                                                    decrementAmount(mov)
                                                }
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}

                        {cart && cart.length === 0 && (
                            <TableRow>
                                <TableCell>
                                    <Box className="py-4">{t('Empty')}</Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box className="mt-6 text-right">
                <Box>
                    {t('Net')}:{' '}
                    {formatPriceByLocale(
                        totalNet,
                        cxt?.sc?.city?.country?.currency,
                    )}
                </Box>
                <Box>
                    {t('VAT')}:{' '}
                    {formatPriceByLocale(
                        totalVat,
                        cxt?.sc?.city?.country?.currency,
                    )}
                </Box>
                <Box>
                    {t('Total')}:{' '}
                    <b>
                        {formatPriceByLocale(
                            total,
                            cxt?.sc?.city?.country?.currency,
                        )}
                    </b>
                </Box>
            </Box>
        </>
    );
}
